import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid3X3, List, Play, Search, Settings, Tv } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ChannelLogo } from './ChannelLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChannelDirectory = ({ selectedChannelId }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/channels`);
      setChannels(response.data);
    } catch (error) {
      console.error('Error fetching channels:', error);
      if (error.response?.status === 401) {
        logout();
      } else {
        toast.error('Erro ao carregar canais');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredChannels = channels.filter(channel =>
    channel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (channel.description && channel.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (channel.category && channel.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <aside id="client-channels" className="client-directory" aria-labelledby="client-channels-title" data-testid="client-channel-directory">
      <div className="client-directory-heading">
        <div><Tv aria-hidden="true" /><h2 id="client-channels-title">Canais Disponíveis</h2><span className="client-channel-count" data-testid="client-channel-count">{channels.length}</span></div>
        <div className="client-view-options" role="group" aria-label="Visualização dos canais">
          <Button variant="ghost" className="client-icon-button" onClick={() => setViewMode('grid')} aria-pressed={viewMode === 'grid'} aria-label="Visualização em grade" title="Visualização em grade" data-testid="client-view-grid"><Grid3X3 aria-hidden="true" /></Button>
          <Button variant="ghost" className="client-icon-button" onClick={() => setViewMode('list')} aria-pressed={viewMode === 'list'} aria-label="Visualização em lista" title="Visualização em lista" data-testid="client-view-list"><List aria-hidden="true" /></Button>
        </div>
      </div>
      <div className="client-search">
        <Search aria-hidden="true" />
        <Input id="client-channel-search" aria-label="Buscar canais" type="search" placeholder="Buscar canais..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="client-channel-search" />
      </div>
      <div className={`client-channel-list client-channel-list--${viewMode}`} data-testid="client-channel-list" aria-busy={loading}>
        {loading ? <p className="client-list-message" role="status" data-testid="client-channels-loading">Carregando canais...</p> : filteredChannels.length === 0 ? (
          <div className="client-list-message" data-testid="client-channels-empty"><Tv aria-hidden="true" /><h3>{searchTerm ? 'Nenhum canal encontrado' : 'Nenhum canal disponível'}</h3><p>{searchTerm ? 'Tente buscar por outro termo' : 'Os canais serão adicionados em breve'}</p>{searchTerm && <Button variant="outline" onClick={() => setSearchTerm('')} data-testid="client-clear-search">Limpar busca</Button>}</div>
        ) : filteredChannels.map(channel => (
          <button type="button" key={channel.id} className={`client-channel-item${selectedChannelId === channel.id ? ' is-selected' : ''}`} onClick={() => navigate(`/channel/${channel.id}`)} aria-current={selectedChannelId === channel.id ? 'true' : undefined} data-testid={`client-channel-${channel.id}`}>
            <ChannelLogo channel={channel} testId={`client-channel-logo-${channel.id}`} />
            <span className="client-channel-copy">
              <strong data-testid={`client-channel-name-${channel.id}`}>{channel.name}</strong>
              {channel.description && <span className="client-channel-description" data-testid={`client-channel-description-${channel.id}`}>{channel.description}</span>}
              <span className="client-channel-meta">{channel.category && <span data-testid={`client-channel-category-${channel.id}`}>{channel.category}</span>}<span className="client-live" data-testid={`client-channel-status-${channel.id}`}><i />{selectedChannelId === channel.id ? 'Assistindo' : 'AO VIVO'}</span></span>
            </span>
            <Play className="client-channel-play" aria-hidden="true" />
          </button>
        ))}
      </div>
      <div className="client-directory-footer" data-testid="client-catalog-stats">
        <span data-testid="client-stream-availability"><Play aria-hidden="true" /><strong>24/7</strong> Live Streaming</span>
        <span data-testid="client-stream-quality"><Settings aria-hidden="true" /><strong>HD</strong> Qualidade</span>
      </div>
    </aside>
  );
};